import React, { useState, useEffect, useRef, useMemo, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet, useNavigate, useLocation, useParams, matchPath } from 'react-router-dom';
import Login2               from './pages/Login2/Login2';
import { AvisoNovidadeNotificacoes, BalaoFavorito, BalaoAviso } from './components/AvisosNotificacao/AvisosNotificacao';
import { resetNovidadeAdm } from './pages/Adm/NovidadesAdm';
import NavBar, { isLeverageUser, temAcessoBtgDashboard, SHOW_INICIO, SHOW_MOCK_PAINEL } from './components/NavBar/NavBar';
import { SHOW_DASHBOARD_BTG } from './services/navAbas';
import { Watermark, VisaoToggle, VISAO_INTERNA, VISAO_PUBLICA, VISAO_BTG } from './components/UI/index';
import { getMe, logout as apiLogout, buildUser, garantirSessao, registrarPageview, encerrarSessao, buscarDispositivoLembrado, buscarSessaoGuest, continuarLogado } from './services/authApi';
import { saveFollowed, loadFollowed, clearFollowed, saveIntendedPath, peekIntendedPath, clearIntendedPath, clearHubEscopo } from './services/session';
import { clearIndicadoresCache } from './services/indicadoresCache';
import { getEmissoes, getEmissoesComoConvidado, clearEmissoesCache } from './services/emissoesApi';
import { clearFluxoCaixaCache } from './services/fluxoCaixaApi';
import { clearSaldoDevedorCache } from './services/saldoDevedorCache';
import { clearCarteiraCache } from './services/carteiraCache';
import { defaultPeriod } from './services/period';
import './styles/global.css';
// lazyComRetry: recarrega 1x se o chunk da página sumiu (deploy novo com a aba
// aberta); ErrorBoundary: qualquer erro de render vira aviso, não tela branca.
import { lazyComRetry } from './services/lazyComRetry';
import { PROJETO_LEVERAGE, PROJETO_BTG, btgPainelLiberado, podeVerProjetoBtg, projetoDaEmissao, emissaoNoProjeto } from './services/projetos';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';

const Painel2             = lazyComRetry(() => import('./pages/Painel2/Painel2'));
const Preferencias        = lazyComRetry(() => import('./pages/Preferencias/Preferencias'));

// Página "Preferências" DESATIVADA (2026-09-24): ficou redundante com
// Emissões (/emissoes/escolha), onde a estrela de cada card monta a carteira —
// inclusive no onboarding (0 emissões). O código continua em
// pages/Preferencias/, mas a rota /preferencias só redireciona pra Emissões,
// até por link direto. Pra reativar: true aqui + item de volta em NavBar.jsx.
const SHOW_PREFERENCIAS = false;
// Destino de "montar/editar a carteira" enquanto Preferências está desligada.
const CARTEIRA_PATH = SHOW_PREFERENCIAS ? '/preferencias' : '/emissoes/escolha';
const Detalhe2            = lazyComRetry(() => import('./pages/Detalhe2/Detalhe2'));
const ApiDocs             = lazyComRetry(() => import('./pages/ApiDocs/ApiDocs'));
const Interno             = lazyComRetry(() => import('./pages/Interno/Interno'));
const Ajuda               = lazyComRetry(() => import('./pages/Ajuda/Ajuda'));
const Welcome             = lazyComRetry(() => import('./pages/Welcome/Welcome'));
const Escolha             = lazyComRetry(() => import('./pages/Escolha/Escolha'));
const EmissoesEscolha     = lazyComRetry(() => import('./pages/EmissoesEscolha/EmissoesEscolha'));
const PainelMock          = lazyComRetry(() => import('./pages/PainelMock/PainelMock'));
const PainelBloqueado     = lazyComRetry(() => import('./pages/Painel2/PainelBloqueado'));
const Perfil              = lazyComRetry(() => import('./pages/EditarPerfil/Perfil'));
const DashboardBtg        = lazyComRetry(() => import('./pages/Btg/DashboardBtg'));
const Adm                 = lazyComRetry(() => import('./pages/Adm/Adm'));

function PageLoader({ label = 'Carregando portal…' }) {
  return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#f6f8fc'}}>
      <div style={{display:'flex',alignItems:'center',gap:12,fontSize:14,color:'#adb0b2'}}>
        <div style={{width:20,height:20,border:'2px solid #e5e5e5',borderTopColor:'#9e36ef',borderRadius:'50%',animation:'spin .7s linear infinite'}}/>
        {label}
      </div>
    </div>
  );
}

// ── Guards de rota ─────────────────────────────────────────────
// Definidos no escopo do módulo (não dentro de App) — um componente definido
// dentro do corpo de outro é recriado a cada render, o que faz o React tratá-lo
// como um tipo novo e remontar toda a subárvore, perdendo state a cada render
// do pai. Por isso recebem tudo via props em vez de fechar sobre o state do App.
// Cada um funciona tanto envolvendo um elemento único (`<RequireX><Y/></RequireX>`)
// quanto como layout route sem `children` (usa <Outlet/> nesse caso).

function RequireAuth({ authChecked, authed, children }) {
  const location = useLocation();
  if (!authChecked) return <PageLoader/>;
  if (!authed) {
    // Guarda a rota tentada (ex.: link direto, sessão que expirou no meio do uso)
    // pra PublicOnly poder levar a pessoa de volta pra lá (em vez da tela padrão)
    // assim que ela autenticar — seja por senha, "Continuar como" ou convidado.
    saveIntendedPath(location.pathname);
    return <Navigate to="/login" replace/>;
  }
  return children ?? <Outlet/>;
}

// Conteúdo que abre por link direto como num site público (convidado automático
// se não houver sessão): as abas que um convidado pode navegar — Painel (vê a
// versão bloqueada, PainelBloqueado), lista de Emissões, Doc. API e Ajuda — e a
// página de uma emissão específica (/emissoes/:id[/aba]). Nesta última o front não
// sabe de antemão se aquele id é público — tenta, e quem decide é o backend (lista
// de emissões escopada à sessão, ver findAuthorizedEmissao/EmissaoRoute).
const ROTAS_PUBLICAS = ['/painel', '/painel/:tab', '/emissoes', '/emissoes/:emissaoId/*', '/apidocs', '/ajuda'];
function ehRotaPublica(pathname) {
  return ROTAS_PUBLICAS.some(path => matchPath({ path }, pathname));
}

// Guard do Shell: igual RequireAuth pra tudo, exceto rota pública (ehRotaPublica)
// sem sessão, que nunca para numa tela de login "seca":
//  - navegador com dispositivo lembrado -> /login, que mostra "Continuar como
//    <Nome>?" e depois volta pro link (intended path);
//  - sem dispositivo lembrado -> entra sozinho como convidado (origem
//    'auto_link_publico' no monitor de acesso) e segue pro link. Se a emissão for
//    privada, EmissaoRoute manda pro /login (ver onPrivadaSemSessao em App).
// `exigirLogin`: não tenta convidado automático — link já provou ser privado nesta
// visita (evita laço caso este guard renderize antes da navegação pro /login), ou
// quem estava aqui era um site_user cuja sessão expirou.
function RequireAuthOuPublico({ authChecked, authed, dispositivoLembrado, exigirLogin, onGuestAutomatico, children }) {
  const location = useLocation();
  const publica = ehRotaPublica(location.pathname) && !exigirLogin;
  const entrarComoGuest = authChecked && !authed && publica && !dispositivoLembrado;
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    if (!entrarComoGuest) return;
    let cancelled = false;
    onGuestAutomatico().catch(() => { if (!cancelled) setFalhou(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entrarComoGuest]);

  if (!publica || dispositivoLembrado || falhou) {
    return <RequireAuth authChecked={authChecked} authed={authed}>{children}</RequireAuth>;
  }
  if (!authChecked || !authed) return <PageLoader/>;
  return children ?? <Outlet/>;
}

function RequireOnboarded({ onboarded, children }) {
  if (!onboarded) return <Navigate to={CARTEIRA_PATH} replace/>;
  return children ?? <Outlet/>;
}

// Seletor de visão do /painel (só equipe Leverage — VisaoToggle). Estado local:
// volta pra Interna ao sair da rota.
//  - Pública: troca o Painel pelo que um convidado vê ali (`bloqueado` —
//    PainelBloqueado com as ações de conta desativadas);
//  - BTG: o Painel normal, mas com a carteira (`followed`) recortada pras
//    emissões públicas — um cliente BTG não enxerga as privadas que a equipe
//    acompanha. O projeto BTG não depende de carteira, então fica igual.
// `children` é função de `followed` (o recortado, na visão BTG) e de
// `simulaCliente` (true na visão BTG — o Painel esconde o que é só interno).
function PainelComVisao({ leverage, bloqueado, followed, children }) {
  const [visao, setVisao] = useState(VISAO_INTERNA);
  const [idsPublicos, setIdsPublicos] = useState(null); // Set | null = não buscado
  const comoBtg = leverage && visao === VISAO_BTG;
  useEffect(() => {
    if (!comoBtg || idsPublicos) return undefined;
    let cancelled = false;
    getEmissoesComoConvidado()
      .then(lista => { if (!cancelled) setIdsPublicos(new Set(lista.map(e => e.id))); })
      // Sem o recorte público não dá pra simular — volta pra Interna.
      .catch(() => { if (!cancelled) setVisao(VISAO_INTERNA); });
    return () => { cancelled = true; };
  }, [comoBtg, idsPublicos]);
  const followedPublicos = useMemo(
    () => (idsPublicos ? followed.filter(id => idsPublicos.has(id)) : followed),
    [followed, idsPublicos]);
  if (!leverage) return children(followed);
  const carregando = comoBtg && !idsPublicos;
  return (
    <>
      {visao === VISAO_PUBLICA ? bloqueado
        : carregando ? <PageLoader/>
        : children(comoBtg ? followedPublicos : followed, comoBtg)}
      <VisaoToggle visao={visao} onChange={setVisao} carregando={carregando}/>
    </>
  );
}

function RequireLeverage({ leverage, children }) {
  if (!leverage) return <Navigate to="/painel/enquadramento" replace/>;
  return children ?? <Outlet/>;
}

// Guarda de "Controle de Obrigações" (dashboard BTG) e de /escolha: acessível
// pra equipe Leverage OU cliente BTG autorizado (`btg` = temAcessoBtgDashboard,
// NavBar.jsx — reflete Control.PermissaoUsuariosExternos, não mais o domínio
// do e-mail), nunca pra qualquer outro cliente. Mesmo tratamento do Interno
// (RequireLeverage): quem não tem acesso nem sabe que a tela existe — sem
// menu, sem card em /escolha, redirect direto pro painel se tentar a URL.
// Ainda assim é só a camada de UI — a garantia de verdade (inclusive "nunca
// acessível sem sessão") é reforçada de novo no backend, em GET
// /v1/btg-dashboard (ver DashboardBtg.jsx: fetch prévio + tratamento de
// 401/403, segunda camada pro caso do sinal de UI estar desatualizado).
// RequireAuth (que envolve esta rota) cobre o caso de sessão nenhuma; este
// guard aqui cobre sessão válida mas sem o papel certo.
function RequireBtgAccess({ allowed, children }) {
  if (!allowed) return <Navigate to="/painel/enquadramento" replace/>;
  return children ?? <Outlet/>;
}

function PublicOnly({ authChecked, authed, homePath, honrarQualquerIntended, children }) {
  // Único lugar que decide pra onde ir ao autenticar (login real ou convidado) —
  // login()/enterGuest() só atualizam state, não navegam, pra não competir com este
  // redirect reativo (dois `navigate()` disparados pelo mesmo evento criavam uma
  // corrida; o último a resolver vencia e sempre ganhava o homePath padrão).
  // `peekIntendedPath` é leitura pura (não remove) — precisa ser, porque StrictMode
  // roda o corpo do componente duas vezes em dev; uma leitura destrutiva faria a
  // segunda invocação achar vazio e computar o destino errado. A limpeza de verdade
  // acontece à parte, no efeito abaixo, depois que o valor já foi usado. Com 0
  // emissões não precisa de lógica especial aqui: `RequireOnboarded` (envolve a
  // rota /painel/:tab) redireciona sozinho pra Emissões (CARTEIRA_PATH) depois desse
  // `Navigate`. `homePath` é `/escolha` pra equipe Leverage e cliente BTG (ver `loginHomePath`
  // em App) — nesse caso só honra `intended` se for um link direto pra uma
  // emissão específica (`/emissoes/...`); qualquer outro intended (ex.:
  // `/painel/enquadramento`, salvo pelo RequireAuth de uma visita anterior à mesma
  // aba) cairia justo na tela que /escolha existe pra evitar, então cai no
  // homePath normalmente. Exceção: `honrarQualquerIntended` — sessão que expirou no
  // meio do uso; aí o intended é a tela onde a pessoa estava agora, não um resto
  // de visita anterior, e "Continuar como" tem que devolver ela pra lá.
  useEffect(() => {
    if (authed) clearIntendedPath();
  }, [authed]);

  if (!authChecked) return <PageLoader/>;
  if (authed) {
    const intended = peekIntendedPath();
    const honraIntended = intended && (honrarQualquerIntended || homePath !== '/escolha' || intended.startsWith('/emissoes/'));
    return <Navigate to={honraIntended ? intended : homePath} replace/>;
  }
  return children ?? <Outlet/>;
}

function CatchAllRedirect({ authChecked, authed, homePath }) {
  if (!authChecked) return <PageLoader/>;
  return <Navigate to={authed ? homePath : '/login'} replace/>;
}

// Redireciona /emissoes/:emissaoId (sem aba) pra aba padrão, preservando o id.
function EmissaoIndexRedirect() {
  const { emissaoId } = useParams();
  return <Navigate to={`/emissoes/${emissaoId}/caracteristicas`} replace/>;
}

// Resolve o :emissaoId da URL pro objeto de emissão antes de renderizar Detalhe2 —
// necessário pra suportar F5/link direto (o objeto completo só existia em memória,
// populado ao clicar numa emissão da lista). A autorização de acesso é decidida só
// pelo que o backend devolve (`findAuthorizedEmissao`), nunca por `followed` — isso
// é preferência local (o que a pessoa optou por acompanhar), não controle de acesso.
// Uma emissão pública deve abrir mesmo sem estar nas preferências; uma privada não
// pode abrir mesmo estando (o que nunca deveria acontecer, mas não confiamos nisso).
function EmissaoRoute({ user, guest, onCreateAccount, onLogin, followed, emissao, setEmissao, period, dataKey, onPeriodChange, onAuthError, findAuthorizedEmissao, onFollowedChange, onPrivadaSemSessao, projetoVisao }) {
  const { emissaoId } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // useParams() sempre devolve string, mas o id de uma emissão pode vir como number
  // do backend (ex.: emissao_id: 1) — compara como string dos dois lados pra não
  // dar falso-negativo (`[1].includes("1")` é false com ===, sem essa normalização).
  const isCurrent = String(emissao?.id) === String(emissaoId);
  const [resolving, setResolving] = useState(() => !isCurrent);

  useEffect(() => {
    if (String(emissao?.id) === String(emissaoId)) { setResolving(false); return; }
    let cancelled = false;
    setResolving(true);
    findAuthorizedEmissao(emissaoId).then(em => {
      if (cancelled) return;
      // Não achou na lista que o backend autoriza pra essa sessão (privada e não
      // concedida, id inexistente, ou falha ao verificar) — falha fechado.
      // Convidado automático (link direto, ninguém escolheu entrar sem conta):
      // conteúdo privado exige login de verdade, então vai pro /login e volta
      // pra cá depois (quem decide é App, onPrivadaSemSessao).
      if (!em) {
        if (onPrivadaSemSessao && onPrivadaSemSessao(pathname)) return;
        navigate('/painel/enquadramento', { replace: true });
        return;
      }
      setEmissao(em);
      setResolving(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emissaoId]);

  if (resolving || !period) return <PageLoader/>;
  return (
    <Detalhe2 key={dataKey} user={user} emissao={emissao} followed={followed} period={period} onPeriodChange={onPeriodChange}
      onSwitch={em => { setEmissao(em); window.scrollTo({top:0}); }} onAuthError={onAuthError}
      onFollowedChange={onFollowedChange} guest={guest} onCreateAccount={onCreateAccount} onLogin={onLogin} projetoVisao={projetoVisao}/>
  );
}

// Casca visual (Watermark + NavBar) compartilhada por todas as telas autenticadas
// exceto /login (nem uma coisa nem outra) e /welcome (mostra a NavBar, mas sem o
// Watermark — a tela já tem o próprio fundo em gradiente + blobs, ver Welcome.jsx;
// o NavBar ali é renderizado direto na rota, não por este Shell).
function Shell({ prefsMode, user, guest, onNav, onLogout, onEditPerfil, onCreateAccount, onAjuda, ultimaEmissao, visaoBtgPainel }) {
  const { pathname } = useLocation();
  const isAdm = pathname.startsWith('/adm');
  const showNav = pathname.startsWith('/painel') || pathname.startsWith('/emissoes')
    || pathname.startsWith('/interno') || pathname === '/ajuda' || pathname === '/apidocs'
    || pathname === '/painel-mock' || pathname === '/perfil' || (SHOW_PREFERENCIAS && pathname === '/preferencias' && prefsMode === 'edit');
  // '/adm' não tem item próprio no menu (ver NavBar.jsx) — cai num id que não
  // bate com nenhum item, então nenhum outro fica destacado por engano.
  const active = pathname.startsWith('/emissoes') ? 'emissoes'
    : pathname === '/preferencias' ? 'preferencias'
    : pathname.startsWith('/interno') ? 'interno'
    : pathname === '/ajuda' ? 'ajuda'
    : pathname === '/apidocs' ? 'apidocs'
    : pathname === '/painel-mock' ? 'painel-mock'
    : pathname === '/perfil' ? 'perfil' : 'painel';
  return (
    <div>
      {/* '/adm' é seu próprio ambiente de ferramenta (rail escuro fixo, ver
          AdmRail.jsx/Adm.jsx) — nem a NavBar flutuante nem a marca d'água
          animada do portal aparecem lá (a distinção de ambiente é requisito
          de produto: sinaliza visualmente que quem opera saiu do portal do
          cliente). Adm.jsx cuida do próprio fundo/marca. */}
      {!isAdm && <Watermark opacity={0.06} motion={true}/>}
      {showNav && (
        <NavBar
          active={active}
          onNav={onNav} onLogout={onLogout} user={user} guest={guest}
          onEditPerfil={onEditPerfil}
          onCreateAccount={onCreateAccount}
          onAjuda={onAjuda}
          ultimaEmissao={ultimaEmissao}
          visaoBtgPainel={visaoBtgPainel}
        />
      )}
      <Outlet/>
      {/* Novidade dos avisos por e-mail (favoritar já liga tudo) — só conta
          de verdade, fora do /perfil (é pra lá que ele manda) e do /adm. */}
      {user && !guest && !isAdm && pathname !== '/perfil' && (
        <AvisoNovidadeNotificacoes key={user.email} email={user.email} onIrParaPerfil={onEditPerfil}/>
      )}
      {/* Balão pós-estrela — aqui no Shell pra não sumir ao trocar de página. */}
      {user && !guest && <BalaoFavorito onIrParaPerfil={onEditPerfil}/>}
      {/* Balão de aviso genérico (mostrarBalaoAviso) — convidado também baixa planilha. */}
      <BalaoAviso/>
    </div>
  );
}

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();

  // Card de novidades do /adm (NovidadesAdm.jsx) reabre a cada ENTRADA no
  // /adm — sair da área zera o "já fechei".
  const naAdm = location.pathname.startsWith('/adm');
  useEffect(() => { if (!naAdm) resetNovidadeAdm(); }, [naAdm]);

  const [authChecked, setAuthChecked] = useState(false);
  const [user,      setUser]      = useState(null);
  const [guest,     setGuest]     = useState(false);
  const [loginMode, setLoginMode] = useState('login');
  const [dataKey,   setDataKey]   = useState(0);
  const [followed,  setFollowed]  = useState([]);
  const [emissao,   setEmissao]   = useState(null);
  // Visão Leverage/BTG compartilhada entre /emissoes/escolha e /painel (só vale
  // pra quem vê o projeto BTG — ver podeVerProjetoBtg). null = ninguém escolheu
  // ainda: cai no padrão `projetoVisaoAtual` (abaixo). Abrir uma emissão leva a
  // visão pro projeto dela (efeito logo após `projetoVisaoAtual`), pra
  // "Emissões"/"Painel" voltarem no mesmo projeto.
  const [projetoVisao, setProjetoVisao] = useState(null);
  const [period,    setPeriod]    = useState(defaultPeriod());
  const [prefsMode, setPrefsMode] = useState('onboarding');
  const [tilt] = useState(true);
  const [justLoggedOut, setJustLoggedOut] = useState(false);
  // { nome, sobrenome } quando este navegador tem "Lembrar de mim" válido e não há
  // sessão — Login2 mostra "Continuar como" no lugar do form.
  const [dispositivoLembrado, setDispositivoLembrado] = useState(null);
  // Convidado criado sozinho por link público (RequireAuthOuPublico), não por
  // clique em "Acessar sem conta" — muda o que acontece em conteúdo privado.
  const [guestAuto, setGuestAuto] = useState(false);
  // Convidado que entrou AGORA pelo botão "Acessar sem conta" do /login — só
  // esse caso tira a pessoa do /login (PublicOnly). Convidado que já existia
  // (sessão retomada, link público) e vai pro /login quer entrar/criar conta:
  // não pode ser mandado de volta pro Painel. Zera ao sair do /login.
  const [guestPeloLogin, setGuestPeloLogin] = useState(false);
  // Sessão expirou no meio do uso: ao reautenticar, volta pra tela onde estava
  // mesmo que não seja o destino padrão do papel (ver PublicOnly).
  const [retomarRota, setRetomarRota] = useState(false);
  // Só login de verdade daqui em diante, sem convidado automático em link
  // público: link que se revelou privado (onPrivadaSemSessao) ou sessão de
  // site_user que expirou (sessaoExpirada). Volta a false ao autenticar.
  const [exigirLogin, setExigirLogin] = useState(false);
  // Chave das <Routes>: muda quando a sessão é retomada em silêncio no meio do
  // uso, pra toda tela refazer as requisições que deram 401 (ver sessaoExpirada).
  const [versaoSessao, setVersaoSessao] = useState(0);

  // RequireAuth grava a rota atual como "intended path" sempre que renderiza com
  // authed=false — inclusive nas passagens de render que acontecem logo após o
  // clique em Sair, antes do navigate('/login') assentar. Sem isso, um login (ou
  // entrada como convidado) seguinte reabriria a página protegida que acabamos de
  // sair de propósito. Só zera ao chegar de fato no /login: o react-router v7
  // aplica a navegação numa transition, então a tela anterior (rota pública)
  // ainda renderiza depois do primeiro commit — zerar antes disso fazia
  // RequireAuthOuPublico criar um convidado automático no meio da saída.
  useEffect(() => {
    if (justLoggedOut && location.pathname === '/login') {
      clearIntendedPath();
      setJustLoggedOut(false);
    }
  }, [justLoggedOut, location.pathname]);

  // Restaura sessão verificando o cookie httpOnly no backend (não há mais token no cliente).
  // `authChecked` só marca "a checagem inicial terminou" — os guards de rota esperam por
  // ele antes de decidir redirecionar, senão uma sessão válida em F5 numa rota protegida
  // seria jogada pro /login antes do getMe() responder.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const usuario = await getMe();
        if (cancelled) return;
        const u = buildUser(usuario);
        setUser(u);
        const followedList = loadFollowed();
        if (followedList && followedList.length) {
          setFollowed(followedList);
        } else {
          setFollowed([]);
          setPrefsMode('onboarding');
        }
      } catch (e) {
        // Sem sessão de site_user. Ainda pode haver dispositivo lembrado de um
        // login anterior neste navegador e/ou sessão de convidado válida (F5/aba
        // nova — retoma em vez de criar outra, que inflaria o monitor de acesso).
        // "Lembrar de mim" vem ANTES do convidado: um guest que ficou vivo (ex.:
        // link público aberto antes) não pode esconder a conta lembrada — senão a
        // pessoa segue como visitante até a sessão guest cair. Com "Lembrar de
        // mim" continua sozinho, na mesma URL. Sem, o convidado ativo continua
        // valendo (escolha recente da pessoa); não havendo convidado, /login
        // pergunta "Continuar como" e volta pra esta URL depois (retomarRota).
        const [dispositivo, sessaoGuest] = await Promise.all([buscarDispositivoLembrado(), buscarSessaoGuest()]);
        if (cancelled) return;
        if (dispositivo && dispositivo.automatico) {
          const retomada = await continuarSilencioso();
          if (cancelled) return;
          if (retomada) { retomarSessao(retomada); return; }
        }
        if (sessaoGuest) {
          enterGuest({ automatico: sessaoGuest.origem_sessao === 'auto_link_publico' });
          return;
        }
        if (!dispositivo) return;
        setDispositivoLembrado(dispositivo);
        setRetomarRota(true);
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Monitor de acessos: avisa o backend quando a aba/navegador fecha —
  // /auth/logout só roda no clique em "sair", não cobre esse caso.
  useEffect(() => {
    window.addEventListener('pagehide', encerrarSessao);
    return () => window.removeEventListener('pagehide', encerrarSessao);
  }, []);

  // Monitor de acessos: reporta a URL real do navegador a cada navegação — sem mapeamento
  // paralelo de nomes, o pathname É a rota reportada. 'login' fica de fora: ainda não há
  // sessão/cookie nesse ponto. `guest` conta como autenticado pro fluxo de visitante.
  const authed = !!(user || guest);
  useEffect(() => {
    if (location.pathname !== '/login' && guestPeloLogin) setGuestPeloLogin(false);
  }, [location.pathname, guestPeloLogin]);
  useEffect(() => {
    if (authed) { setRetomarRota(false); setExigirLogin(false); }
  }, [authed]);
  useEffect(() => {
    if (authChecked && authed && location.pathname !== '/login') {
      registrarPageview(location.pathname);
    }
  }, [location.pathname, authChecked, authed]);

  // Destino pós-login. Carteira (dentro do Painel) é a página inicial de
  // cliente comum/convidado. Equipe Leverage e cliente BTG são a exceção: caem
  // em /escolha pra escolher entre as opções do próprio papel (Painel/Interno/
  // Controle de Obrigações pra Leverage; Portal/Controle de Obrigações pra
  // BTG) antes de entrar em qualquer uma delas (Escolha.jsx — Welcome/Início,
  // ainda em construção, não entra nesse fluxo). `homePath` continua sendo o
  // destino de navegações internas (voltar do detalhe, salvar preferências
  // etc.) — só o pós-login (`loginHomePath`, usado no guard de /login abaixo)
  // varia por papel.
  const homePath = '/painel/enquadramento';
  const leverage = isLeverageUser(user);
  const btg = temAcessoBtgDashboard(user);
  const loginHomePath = (leverage || btg) ? '/escolha' : homePath;
  // Quem vê o BTG e não tem favorito (visão BTG não tem carteira) começa em
  // BTG — senão o Painel abriria vazio; o resto começa em Leverage.
  const veBtg = podeVerProjetoBtg(user);
  const projetoVisaoAtual = projetoVisao ?? (veBtg && !followed.length ? PROJETO_BTG : PROJETO_LEVERAGE);
  // Mesmo critério do `visaoBtg` do Painel2, pro dropdown do Painel na NavBar
  // (abas da visão BTG). Sem o `simulaCliente` do VisaoToggle (estado interno
  // do PainelComVisao): simulando cliente, o Painel cai na visão Leverage e
  // normaliza a aba sozinho.
  const visaoBtgPainel = btgPainelLiberado(user) && veBtg && projetoVisaoAtual === PROJETO_BTG;
  // Emissão exibida em mais de um projeto (45/102: dona Leverage, também no
  // BTG — ver emissaoNoProjeto) mantém a visão atual se pertencer a ela; o
  // resto vai pro projeto dono.
  useEffect(() => {
    if (emissao) setProjetoVisao(emissaoNoProjeto(emissao, projetoVisaoAtual) ? projetoVisaoAtual : projetoDaEmissao(emissao));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emissao]);

  // login()/enterGuest() só atualizam state — não navegam. Quem decide o destino é
  // o guard PublicOnly (que envolve /login), reagindo a `authed` virar true. Chamar
  // navigate() aqui também criaria uma corrida entre duas navegações disparadas pelo
  // mesmo evento (a explícita daqui e a reativa do PublicOnly) — a última a resolver
  // vencia, e era sempre a do PublicOnly, ignorando silenciosamente o destino tentado.
  const login = ({ user: u, followed: prefFollowed }) => {
    setUser(u);
    setGuest(false);
    setGuestAuto(false);
    setDispositivoLembrado(null);
    setLoginMode('login');
    setPeriod(defaultPeriod());
    saveFollowed(prefFollowed);
    const hasFollowed = !!(prefFollowed && prefFollowed.length);
    setFollowed(hasFollowed ? prefFollowed : []);
    if (!hasFollowed) setPrefsMode('onboarding');
  };
  const enterGuest = ({ automatico = false } = {}) => {
    setUser(null);
    setGuest(true);
    setGuestAuto(automatico);
    setLoginMode('login');
    setPeriod(defaultPeriod());
    setFollowed([]);
    setPrefsMode('onboarding');
  };
  // Caches de dado são escopados à sessão (o que ela pode ver) — limpar sempre
  // que a sessão acaba, por qualquer motivo: quem entrar depois pode ser outra conta.
  const limparCachesDaSessao = () => {
    clearIndicadoresCache();
    clearEmissoesCache();
    clearFluxoCaixaCache();
    clearSaldoDevedorCache();
    clearCarteiraCache();
  };
  const limparEstadoDaSessao = () => {
    limparCachesDaSessao();
    setUser(null); setGuest(false); setGuestAuto(false); setGuestPeloLogin(false); setEmissao(null); setProjetoVisao(null);
  };
  // "Sair" explícito: /auth/logout também esquece o dispositivo lembrado — o
  // próximo acesso pede senha.
  const logout = () => {
    apiLogout().catch(() => {});
    clearFollowed();
    clearHubEscopo();
    limparEstadoDaSessao();
    setDispositivoLembrado(null);
    setJustLoggedOut(true);
    navigate('/login');
  };
  // "Continuar como" sem clique ("Lembrar de mim"). Deduplicado: várias
  // requisições dando 401 juntas (ou boot + expiração) viram UMA chamada — o
  // token de dispositivo rotaciona a cada uso, uma segunda chamada com o token
  // velho seria recusada. Se ainda assim perder a corrida (outra aba continuou
  // primeiro), a sessão nova já está no cookie: confere com getMe antes de
  // desistir. Devolve { user, preferenciasEmissao } ou null.
  const continuarEmVoo = useRef(null);
  const continuarSilencioso = () => {
    if (!continuarEmVoo.current) {
      continuarEmVoo.current = (async () => {
        try {
          return await continuarLogado({ automatico: true });
        } catch (e) {
          if (e.status !== 401) return null;
          try { return { user: buildUser(await getMe()), preferenciasEmissao: null }; } catch (e2) { return null; }
        }
      })().finally(() => { continuarEmVoo.current = null; });
    }
    return continuarEmVoo.current;
  };
  // Aplica uma sessão retomada ("Continuar como" automático) sem mexer no que a
  // pessoa estava fazendo — diferente de login(), não reseta o período nem
  // navega. Preferências: as do servidor quando vieram (/continuar), senão o
  // cache local (caso getMe acima).
  const retomarSessao = ({ user: u, preferenciasEmissao }) => {
    setUser(u);
    setGuest(false);
    setGuestAuto(false);
    setDispositivoLembrado(null);
    const lista = preferenciasEmissao || loadFollowed() || [];
    if (preferenciasEmissao) saveFollowed(preferenciasEmissao);
    setFollowed(lista);
    if (!lista.length) setPrefsMode('onboarding');
  };
  // Sessão que só expirou (401 no meio do uso). NÃO chama /auth/logout — isso
  // revogaria o dispositivo lembrado justo quando ele é útil.
  //  - "Lembrar de mim": continua sozinho e remonta as telas (dataKey) pra
  //    refazer as requisições — a pessoa fica onde estava, sem ver nada.
  //  - Sem: busca o dispositivo antes de limpar o estado, pra /login já abrir
  //    direto em "Continuar como" (sem piscar o form); RequireAuth guarda a rota
  //    atual e PublicOnly devolve a pessoa pra ela depois.
  // Deduplicado como continuarSilencioso: uma tela costuma disparar várias
  // requisições, e todas dão 401 juntas.
  const expiracaoEmVoo = useRef(null);
  const sessaoExpirada = () => {
    if (!expiracaoEmVoo.current) {
      expiracaoEmVoo.current = (async () => {
        const dispositivo = await buscarDispositivoLembrado();
        if (dispositivo && dispositivo.automatico) {
          const retomada = await continuarSilencioso();
          if (retomada) {
            retomarSessao(retomada);
            // Remonta todas as rotas, não só as que usam dataKey: telas como
            // DashboardBtg/Interno/Adm tratam o 401 só chamando onAuthError e
            // ficariam paradas no erro, sem refazer a requisição.
            setVersaoSessao(v => v + 1);
            return;
          }
        }
        setDispositivoLembrado(dispositivo);
        setRetomarRota(true);
        // Quem estava logado numa emissão não pode virar convidado em silêncio
        // (perderia o acesso ao que é privado sem aviso) — vai pro login.
        setExigirLogin(true);
        limparEstadoDaSessao();
      })().finally(() => { expiracaoEmVoo.current = null; });
    }
    return expiracaoEmVoo.current;
  };
  // Link público sem sessão nem dispositivo lembrado: entra como convidado
  // sozinho. garantirSessao() restaura antes de criar (outra aba pode ter aberto
  // sessão desde o boot) e deduplica chamadas simultâneas (StrictMode etc.).
  const entrarComoGuestAutomatico = () => garantirSessao({ automatico: true }).then(aplicarSessao);
  // Emissão não autorizada pra um convidado automático: conteúdo privado exige
  // login de verdade — manda pro /login e volta pro link depois. Devolve false
  // (EmissaoRoute segue o fluxo normal) pra convidado que escolheu entrar sem
  // conta ou pra quem já está logado.
  const onPrivadaSemSessao = path => {
    if (!guest || !guestAuto) return false;
    saveIntendedPath(path);
    setExigirLogin(true);
    setGuest(false); setGuestAuto(false);
    navigate('/login', { replace: true });
    return true;
  };
  // "Entrar"/"Criar conta" a partir de conteúdo público. Convidado NÃO passa por
  // logout(): /auth/login e /auth/continuar já encerram sozinhos a sessão de
  // convidado que substituem, e encerrar antes deixava a tela pública sem sessão
  // por um instante — o bastante pra abrir outro convidado automático. O estado
  // de convidado continua valendo; no /login PublicOnly não o trata como
  // autenticado (só guestPeloLogin), e "Acessar sem conta" reaproveita a sessão.
  const irParaLogin = () => {
    if (!guest) { logout(); return; }
    // Caches escopados ao convidado não podem vazar pra conta que vai entrar.
    limparCachesDaSessao();
    setEmissao(null); setProjetoVisao(null);
    clearIntendedPath();
    navigate('/login');
  };
  const createAccount = () => { setLoginMode('signup'); irParaLogin(); };
  // Resultado de garantirSessao() (authApi): site_user (logou em outra aba) ou
  // convidado, reaproveitado ou novo.
  const aplicarSessao = sessao => {
    if (sessao.tipo === 'site_user') {
      retomarSessao({ user: buildUser(sessao.usuario), preferenciasEmissao: null });
      return;
    }
    enterGuest({ automatico: sessao.automatico });
  };

  // Em modo visitante a sessão (cookie guest) é renovada pelo backend enquanto há uso,
  // mas expira depois de um tempo parado (ou no teto) — ao receber 401, abre outra sessão e força um remonte das telas (via dataKey) para
  // refazer as requisições, em vez de jogar o visitante de volta pro login.
  // Antes de abrir outro convidado, mesma prioridade do boot: "Lembrar de mim"
  // válido neste navegador continua como a conta lembrada. Deduplicado como
  // sessaoExpirada (uma tela dá vários 401 juntos) — sem isso cada 401 faria
  // seu próprio peek e o segundo, chegando depois da rotação, abriria outra
  // sessão de site_user.
  const guestExpiradoEmVoo = useRef(null);
  const handleAuthError = () => {
    if (!guest) return sessaoExpirada();
    if (!guestExpiradoEmVoo.current) {
      guestExpiradoEmVoo.current = (async () => {
        const dispositivo = await buscarDispositivoLembrado();
        if (dispositivo && dispositivo.automatico) {
          const retomada = await continuarSilencioso();
          if (retomada) {
            retomarSessao(retomada);
            setVersaoSessao(v => v + 1);
            return;
          }
        }
        let sessao;
        try {
          sessao = await garantirSessao({ automatico: guestAuto });
        } catch (e) { /* sessão de visitante não renovou — cai para o logout abaixo */ }
        if (!sessao) { logout(); return; }
        if (sessao.tipo === 'site_user') {
          aplicarSessao(sessao);
          setVersaoSessao(v => v + 1);
          return;
        }
        // Convidado ainda válido: o 401 não foi expiração (ex.: rota que exige
        // conta) — remontar só repetiria o 401 num laço. Só remonta se a
        // sessão é nova.
        if (sessao.criada) setDataKey(k => k + 1);
      })().finally(() => { guestExpiradoEmVoo.current = null; });
    }
    return guestExpiradoEmVoo.current;
  };

  const savePrefs = ids => { setFollowed(ids); if (!guest) saveFollowed(ids); navigate('/painel/enquadramento'); window.scrollTo({top:0}); };
  // Mesma atualização de savePrefs (estado + cache local), sem navegar — usado pela
  // estrela de "seguir" em /emissoes/escolha, que persiste sem sair da tela.
  const updateFollowed = ids => { setFollowed(ids); if (!guest) saveFollowed(ids); };
  const editPrefs = () => { setPrefsMode('edit'); navigate(CARTEIRA_PATH); window.scrollTo({top:0}); };

  const openEmissao = em => { setEmissao(em); navigate(`/emissoes/${em.id}/caracteristicas`); window.scrollTo({top:0}); };
  const backToPainel = () => { navigate('/painel/enquadramento'); window.scrollTo({top:0}); };
  const openAjuda = () => { navigate('/ajuda'); window.scrollTo({top:0}); };
  const openPerfil = () => { navigate('/perfil'); window.scrollTo({top:0}); };

  // Verifica se a sessão atual está autorizada a ver essa emissão — usado só pra
  // quem cai direto numa URL de emissão (F5, link compartilhado, id digitado a mão),
  // onde não dá pra confiar que o id veio de um lugar já validado. GET /v1/emissoes/
  // já é escopado pelo backend à sessão (cookie); uma emissão privada sem permissão
  // simplesmente não aparece nessa lista. Falha fechado: não achou ou a busca falhou
  // → trata como não autorizado (nunca renderiza a página nesse caso). `ativa` não
  // entra: visibilidade é só a liberação manual (/adm/liberacao-site).
  const findAuthorizedEmissao = async id => {
    try {
      const lista = await getEmissoes();
      return lista.find(e => String(e.id) === String(id)) || null;
    } catch (e) {
      return null;
    }
  };

  // Clicar em "Emissões" sempre cai na tela de escolha primeiro — nunca entra
  // direto numa emissão específica. Só um link direto (URL /emissoes/:id/:tab
  // compartilhada, F5, id digitado) abre de cara, sem passar por aqui: quem
  // decide essa autorização é EmissaoRoute via findAuthorizedEmissao, não isto.
  const onNav = id => {
    if (id==='inicio') { navigate('/welcome'); window.scrollTo({top:0}); }
    else if (id==='painel') backToPainel();
    else if (id==='preferencias') editPrefs();
    else if (id==='apidocs') { navigate('/apidocs'); window.scrollTo({top:0}); }
    else if (id==='painel-mock') { navigate('/painel-mock'); window.scrollTo({top:0}); }
    else if (id==='interno') { if (isLeverageUser(user)) { navigate('/interno'); window.scrollTo({top:0}); } }
    else if (id==='dashboard-btg') { if (temAcessoBtgDashboard(user)) { navigate('/btg/dashboard-btg'); window.scrollTo({top:0}); } }
    else if (id==='emissoes') {
      // Sempre a lista (EmissoesEscolha funciona com `followed` vazio — é lá
      // que se marca a carteira, inclusive no onboarding).
      if (SHOW_PREFERENCIAS && !followed.length && !guest) { editPrefs(); return; }
      navigate('/emissoes/escolha');
      window.scrollTo({top:0});
    }
  };

  const emissoesIndexTarget = (SHOW_PREFERENCIAS && !followed.length && !guest) ? '/preferencias' : '/emissoes/escolha';

  return (
    <ErrorBoundary resetKey={location.pathname}>
    <Suspense fallback={<PageLoader/>}>
      <Routes key={versaoSessao}>
        <Route path="/login" element={
          <PublicOnly authChecked={authChecked} authed={!!user || (guest && guestPeloLogin)} homePath={loginHomePath} honrarQualquerIntended={retomarRota}>
            <Login2 onLogin={login} onGuest={sessao => {
                // Clique em "Acessar sem conta" = convidado por escolha, mesmo
                // que tenha reaproveitado uma sessão aberta sozinha antes.
                if (sessao.tipo === 'site_user') { aplicarSessao(sessao); return; }
                enterGuest(); setGuestPeloLogin(true);
              }} initialMode={loginMode}
              dispositivoLembrado={dispositivoLembrado} onDispositivoEsquecido={() => setDispositivoLembrado(null)}/>
          </PublicOnly>
        }/>

        {/* Destino pós-login da equipe Leverage e do cliente BTG (`loginHomePath`
            acima) — sem NavBar/Shell, igual /login (mesmo padrão de antes de
            existir Welcome/Início). Fora do RequireOnboarded de propósito: os
            destinos possíveis (Painel/Interno/Controle de Obrigações) já lidam
            com 0 emissões por conta própria (RequireOnboarded mais abaixo).
            RequireBtgAccess fecha a porta pra quem digitar a URL direto sem
            ser de uma dessas duas audiências. */}
        <Route path="/escolha" element={
          <RequireAuth authChecked={authChecked} authed={authed}>
            <RequireBtgAccess allowed={leverage || btg}>
              <Escolha user={user} isLeverage={leverage}
                onPainel={() => { navigate('/painel/enquadramento'); window.scrollTo({top:0}); }}
                onInterno={() => { navigate('/interno'); window.scrollTo({top:0}); }}
                onControleObrigacoes={SHOW_DASHBOARD_BTG ? () => { navigate('/btg/dashboard-btg'); window.scrollTo({top:0}); } : null}
                onAdm={() => { navigate('/adm'); window.scrollTo({top:0}); }}
              />
            </RequireBtgAccess>
          </RequireAuth>
        }/>

        {/* "Controle de Obrigações" — igual a /interno, fora do Shell de
            propósito (sem NavBar): cliente BTG não pode ver nenhum item de
            menu do portal Leverage nessa tela, nem a equipe Leverage precisa
            deles aqui. allowed usa o mesmo par leverage||btg de /escolha —
            time interno continua enxergando, cliente BTG só isto e /painel. */}
        {/* Com SHOW_DASHBOARD_BTG desligada (navAbas.js), link direto cai no
            homePath pra todo mundo, inclusive equipe Leverage. */}
        <Route path="/btg/dashboard-btg" element={!SHOW_DASHBOARD_BTG ? <Navigate to={homePath} replace/> :
          <RequireAuth authChecked={authChecked} authed={authed}>
            <RequireBtgAccess allowed={leverage || btg}>
              <DashboardBtg onVoltar={() => { navigate('/escolha'); window.scrollTo({top:0}); }} onAuthError={handleAuthError} onLogout={logout}/>
            </RequireBtgAccess>
          </RequireAuth>
        }/>

        {/* Fora do RequireOnboarded de propósito, igual /preferencias e /emissoes/:id
            abaixo: quando SHOW_INICIO está ligada, precisa ser alcançável mesmo
            com 0 emissões, pra mostrar a mensagem + CTA de montar a carteira em
            vez de um redirect silencioso. Com a flag desligada (padrão hoje),
            nem chega a renderizar: link direto pra /welcome cai no homePath
            (RequireOnboarded, mais abaixo, cuida do caso "0 emissões" a
            partir daí). */}
        <Route path="/welcome" element={
          <RequireAuth authChecked={authChecked} authed={authed}>
            {SHOW_INICIO ? (
              <>
                <NavBar active="inicio" onNav={onNav} onLogout={logout} user={user} guest={guest}
                  onEditPerfil={openPerfil} onCreateAccount={createAccount} onAjuda={openAjuda} ultimaEmissao={emissao}/>
                <Welcome user={user} guest={guest} followed={followed} isLeverage={isLeverageUser(user)}
                  onNavigate={path => { navigate(path); window.scrollTo({top:0}); }}
                  onBuildCarteira={() => { navigate(CARTEIRA_PATH); window.scrollTo({top:0}); }}
                />
              </>
            ) : (
              <Navigate to={homePath} replace/>
            )}
          </RequireAuth>
        }/>

        {/* RequireAuthOuPublico: igual RequireAuth, exceto link direto de emissão
            sem sessão — "Continuar como" ou convidado automático em vez do login.
            `justLoggedOut`: no "Sair"/"Criar conta" a tela anterior (ex. /painel,
            rota pública) ainda renderiza uma vez sem sessão antes do /login —
            sem isso viraria um convidado automático no meio da saída. */}
        <Route element={
          <RequireAuthOuPublico authChecked={authChecked} authed={authed}
            dispositivoLembrado={dispositivoLembrado} exigirLogin={exigirLogin || justLoggedOut} onGuestAutomatico={entrarComoGuestAutomatico}>
            <Shell prefsMode={prefsMode} user={user} guest={guest} onNav={onNav} onLogout={logout}
              onEditPerfil={openPerfil} onCreateAccount={createAccount} onAjuda={openAjuda} ultimaEmissao={emissao}
              visaoBtgPainel={visaoBtgPainel}/>
          </RequireAuthOuPublico>
        }>
          {/* Sem RequireOnboarded de propósito, mesmo padrão de /welcome: dados fixos,
              não depende de `followed`. Com a flag desligada, link direto cai no
              homePath em vez de renderizar (ver SHOW_MOCK_PAINEL, NavBar.jsx). */}
          <Route path="/painel-mock" element={SHOW_MOCK_PAINEL ? <PainelMock/> : <Navigate to={homePath} replace/>}/>

          <Route path="/preferencias" element={
            !SHOW_PREFERENCIAS ? <Navigate to="/emissoes/escolha" replace/> : period && (
              <Preferencias key={dataKey} user={user} initial={followed} isOnboarding={prefsMode==='onboarding'} isGuest={guest}
                onSave={savePrefs} onCancel={prefsMode==='edit'?backToPainel:null} onAuthError={handleAuthError}/>
            )
          }/>

          {/* Perfil (dados, senha, avisos por e-mail) — antes era um modal
              (EditarPerfilModal). Fora do RequireOnboarded: dá pra editar o
              perfil sem carteira montada (a matriz de avisos só mostra um
              convite pra escolher emissões). Convidado não tem perfil — a rota
              de preferências de aviso exige site_user. */}
          <Route path="/perfil" element={
            guest || !user ? <Navigate to={homePath} replace/> : (
              <Perfil key={dataKey} user={user} followed={followed} onSave={updated => setUser(updated)}
                onAuthError={handleAuthError} onIrParaEmissoes={() => { navigate('/emissoes/escolha'); window.scrollTo({top:0}); }}/>
            )
          }/>

          {/* Fora do RequireOnboarded de propósito: um link direto pra uma emissão
              autorizada tem que abrir mesmo pra quem nunca marcou emissões —
              "seguir" é conveniência pessoal, não controle de acesso. A autorização
              de verdade é decidida dentro de EmissaoRoute via findAuthorizedEmissao. */}
          <Route path="/emissoes" element={<Navigate to={emissoesIndexTarget} replace/>}/>
          <Route path="/emissoes/escolha" element={
            <EmissoesEscolha user={user} followed={followed} guest={guest} isLeverage={leverage} onFollowedChange={updateFollowed} onSelect={openEmissao} onAuthError={handleAuthError}
              onCreateAccount={createAccount} onLogin={irParaLogin}
              projeto={projetoVisaoAtual} onProjetoChange={setProjetoVisao}/>
          }/>
          <Route path="/emissoes/:emissaoId" element={<EmissaoIndexRedirect/>}/>
          <Route path="/emissoes/:emissaoId/:tab" element={
            <EmissaoRoute user={user} guest={guest} onCreateAccount={createAccount} onLogin={irParaLogin} followed={followed} emissao={emissao} setEmissao={setEmissao}
              period={period} dataKey={dataKey} onPeriodChange={setPeriod}
              onAuthError={handleAuthError} findAuthorizedEmissao={findAuthorizedEmissao} onFollowedChange={updateFollowed}
              onPrivadaSemSessao={onPrivadaSemSessao} projetoVisao={projetoVisaoAtual}/>
          }/>

          {/* Painel: convidado não tem carteira (nada é salvo), então vê o Painel
              borrado com convite pra criar conta (PainelBloqueado) em vez de ser
              mandado pro onboarding — por isso fora do galho RequireOnboarded,
              que continua valendo só pra quem tem conta. */}
          <Route path="/painel" element={<Navigate to="/painel/enquadramento" replace/>}/>
          <Route path="/painel/:tab" element={
            guest ? (
              <PainelBloqueado onCreateAccount={createAccount} onLogin={irParaLogin}
                onVerEmissoes={() => { navigate('/emissoes/escolha'); window.scrollTo({top:0}); }}/>
            ) : (
              <PainelComVisao leverage={leverage} followed={followed} bloqueado={
                <PainelBloqueado desativado onCreateAccount={createAccount} onLogin={irParaLogin}
                  onVerEmissoes={() => { navigate('/emissoes/escolha'); window.scrollTo({top:0}); }}/>
              }>
                {/* Quem vê o BTG entra mesmo sem favorito (se btgPainelLiberado): a visão BTG mostra
                    todas as emissões do projeto, não depende da carteira. */}
                {(followedDaVisao, simulaCliente) => (
                  <RequireOnboarded onboarded={!!followed.length || (btgPainelLiberado(user, simulaCliente) && veBtg)}>
                    {period && (
                      <Painel2 key={dataKey} user={user} followed={followedDaVisao} period={period} onPeriodChange={setPeriod}
                        projeto={projetoVisaoAtual} onProjetoChange={setProjetoVisao} simulaCliente={simulaCliente}
                        onOpenEmissao={openEmissao} onEditPrefs={editPrefs} tilt={tilt} onAuthError={handleAuthError}/>
                    )}
                  </RequireOnboarded>
                )}
              </PainelComVisao>
            )
          }/>

          {/* Doc. API e Ajuda não dependem de carteira: convidado abre direto.
              Quem tem conta segue passando pelo onboarding, como antes. */}
          <Route path="/apidocs" element={
            <RequireOnboarded onboarded={guest || !!followed.length}><ApiDocs/></RequireOnboarded>
          }/>
          <Route path="/ajuda" element={
            <RequireOnboarded onboarded={guest || !!followed.length}><Ajuda user={user} onNav={onNav}/></RequireOnboarded>
          }/>

          <Route element={<RequireOnboarded onboarded={!!followed.length}/>}>

            <Route path="/interno" element={
              <RequireLeverage leverage={isLeverageUser(user)}>
                <Interno user={user} followed={followed} onOpenEmissao={openEmissao} onAuthError={handleAuthError} onLogout={logout}/>
              </RequireLeverage>
            }/>
            <Route path="/interno/:tab" element={
              <RequireLeverage leverage={isLeverageUser(user)}>
                <Interno user={user} followed={followed} onOpenEmissao={openEmissao} onAuthError={handleAuthError} onLogout={logout}/>
              </RequireLeverage>
            }/>

            {/* "Adm" (liberações de acesso privado, Control.PermissaoUsuariosExternos)
                — galho próprio, fora da Área Interna, mesmo gate de domínio
                (RequireLeverage) de /interno. Sem item no NavBar — alcançável
                pelo card destacado em /escolha (ver Escolha.jsx, só pra quem
                isLeverage) ou por link direto. /adm/:subTab cobre a
                sub-navegação própria do Adm (ex. /adm/liberacoes, ver Adm.jsx);
                /adm/:subTab/:acao cobre o único sub-item hoje (nova liberação,
                ex. /adm/liberacoes/nova) — 1 nível só, ver AdmRail.jsx. */}
            <Route path="/adm" element={
              <RequireLeverage leverage={isLeverageUser(user)}>
                <Adm user={user} onLogout={logout} onAuthError={handleAuthError}/>
              </RequireLeverage>
            }/>
            <Route path="/adm/:subTab" element={
              <RequireLeverage leverage={isLeverageUser(user)}>
                <Adm user={user} onLogout={logout} onAuthError={handleAuthError}/>
              </RequireLeverage>
            }/>
            <Route path="/adm/:subTab/:acao" element={
              <RequireLeverage leverage={isLeverageUser(user)}>
                <Adm user={user} onLogout={logout} onAuthError={handleAuthError}/>
              </RequireLeverage>
            }/>
          </Route>
        </Route>

        <Route path="*" element={<CatchAllRedirect authChecked={authChecked} authed={authed} homePath={homePath}/>}/>
      </Routes>

    </Suspense>
    </ErrorBoundary>
  );
}
